"use client";

import { forwardRef } from "react";
import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";

export function cn(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

/* ---------------------------------------------------------------- Panel
   The base surface. `lit` adds the top bevel that carries layering in
   dark mode, where shadows do almost nothing. */

export function Panel({
  lit = true,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & { lit?: boolean }) {
  return (
    <div
      className={cn(
        "bg-panel border border-hairline rounded-2xl",
        lit && "shadow-[var(--lift-1),var(--bevel)]",
        className
      )}
      {...props}
    />
  );
}

export function PanelHead({
  eyebrow,
  title,
  meta,
  action,
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-start justify-between gap-4 px-5 pt-4 pb-3.5", className)}>
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
        <h2 className="text-[15px] font-semibold text-ink leading-tight tracking-[-0.01em]">
          {title}
        </h2>
        {meta && <div className="text-[12.5px] text-ink-3 mt-1">{meta}</div>}
      </div>
      {action}
    </div>
  );
}

/* --------------------------------------------------------------- Button */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "signal" | "quiet" | "ghost";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
};

const VARIANTS: Record<string, string> = {
  signal:
    "bg-signal text-signal-ink hover:bg-signal-lit shadow-[var(--lift-1)] " +
    "disabled:bg-sunken disabled:text-ink-3 disabled:shadow-none",
  quiet:
    "bg-raised text-ink border border-hairline hover:border-hairline-lit " +
    "disabled:text-ink-3",
  ghost: "text-ink-2 hover:text-ink hover:bg-raised disabled:text-ink-3",
};

const SIZES: Record<string, string> = {
  sm: "h-8 px-3 text-[12.5px] gap-1.5 rounded-lg",
  md: "h-9.5 px-4 text-[13.5px] gap-2 rounded-[10px]",
  lg: "h-12 px-6 text-[14.5px] gap-2.5 rounded-xl",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "signal", size = "md", loading, className, children, disabled, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center font-medium",
        "transition-[background-color,border-color,color,transform] duration-150",
        "disabled:cursor-not-allowed active:scale-[0.98]",
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  );
});

/* ---------------------------------------------------------------- Field */

export const Field = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Field({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          "w-full h-10 px-3.5 rounded-[10px] bg-sunken border border-hairline",
          "text-[13.5px] text-ink placeholder:text-ink-3 transition-colors",
          "hover:border-hairline-lit focus:border-signal focus:bg-panel",
          className
        )}
        {...props}
      />
    );
  }
);

export function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="eyebrow block mb-2">
      {children}
    </label>
  );
}

/* ------------------------------------------------------------------ Tag
   Status colour always ships with a label and a dot — that pairing is
   what lets these hues sit inside the CVD warn band safely. */

export type Tone = "neutral" | "signal" | "hold" | "alert";

const TAG_TONES: Record<Tone, string> = {
  neutral: "text-ink-2 bg-sunken border-hairline",
  signal: "text-signal bg-signal-wash border-signal-edge",
  hold: "text-hold bg-hold-wash border-transparent",
  alert: "text-alert bg-alert-wash border-transparent",
};

export function Tag({
  tone = "neutral",
  dot = true,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 h-6 px-2 rounded-md border",
        "text-[11.5px] font-medium whitespace-nowrap",
        TAG_TONES[tone]
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/* --------------------------------------------------------------- Notice */

const NOTICE_TONES: Record<Tone, string> = {
  neutral: "bg-sunken border-hairline text-ink-2",
  signal: "bg-signal-wash border-signal-edge text-signal",
  hold: "bg-hold-wash border-transparent text-hold",
  alert: "bg-alert-wash border-transparent text-alert",
};

export function Notice({
  tone = "neutral",
  icon,
  title,
  children,
  action,
}: {
  tone?: Tone;
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={cn("flex items-start gap-3 p-3.5 rounded-xl border", NOTICE_TONES[tone])}>
      {icon && <span className="shrink-0 mt-px">{icon}</span>}
      <div className="flex-1 min-w-0">
        {title && <p className="text-[13px] font-semibold leading-snug">{title}</p>}
        {children && (
          <div className={cn("text-[12.5px] leading-relaxed opacity-95", title && "mt-1")}>
            {children}
          </div>
        )}
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------- Skeleton */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton rounded-lg", className)} />;
}

/* ------------------------------------------------------------ Stat
   A hero number, not a chart. Display serif carries the value. */

export function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="px-4 py-3.5 rounded-xl bg-sunken border border-hairline">
      <p className="eyebrow">{label}</p>
      <p className="figure text-[26px] leading-none text-ink mt-2">{value}</p>
      {hint && <p className="text-[11.5px] text-ink-3 mt-1.5">{hint}</p>}
    </div>
  );
}
